import { useEffect, useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import type { Client } from '@salonomy/types';
import { Avatar, Button, EmptyState, Pill, TextInput } from '@salonomy/ui';
import { CLIENTS_PAGE, useClients } from '../data/clients.js';
import { useAuth } from '../lib/auth.js';
import { fullName, initials, maskPhone, money } from '../lib/format.js';
import { ClientModal } from '../features/clients/ClientModal.js';
import { ImportClientsModal } from '../features/clients/ImportClientsModal.js';
import { LoyaltyBalanceStrip } from '../features/clients/LoyaltyBalanceStrip.js';
import { MembershipStrip } from '../features/clients/MembershipStrip.js';
import { ConsentPanel } from '../features/clients/ConsentPanel.js';
import { PhotoGallery } from '../features/photos/PhotoGallery.js';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function patchTestState(client: Client): 'due' | 'soon' | null {
  if (!client.patchTestDueAt) return null;
  const due = new Date(`${client.patchTestDueAt}T00:00:00Z`).getTime();
  const now = Date.now();
  if (due <= now) return 'due';
  if (due - now <= 14 * 86_400_000) return 'soon';
  return null;
}

export function ClientsPage() {
  const { user, isManager } = useAuth();
  // Bulk import mirrors the server RBAC: owner/manager (the floor book) or a chair_renter (their own
  // sealed book). A plain stylist/receptionist has no bulk-import surface.
  const canImport = isManager || user?.renterId != null;
  // Read ?q= reactively so a deep-link (the command palette / diary "view client") drives the filter
  // even when ClientsPage is already mounted — the old mount-only useState silently ignored it.
  const routeSearch = useSearch({ strict: false }) as { q?: string };
  const [q, setQ] = useState(routeSearch.q ?? '');
  useEffect(() => {
    if (typeof routeSearch.q === 'string') setQ(routeSearch.q);
  }, [routeSearch.q]);
  const [offset, setOffset] = useState(0);
  const debouncedQ = useDebounced(q, 250);
  const { data, isLoading } = useClients(debouncedQ, offset);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Client | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const clients = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const selected = useMemo(
    () => clients.find((c) => c.id === selectedId) ?? clients[0] ?? null,
    [clients, selectedId],
  );

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Clients</div>
          <span className="muted">{total} on the book</span>
        </div>
        <div className="topbar__right">
          <TextInput
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            placeholder="Search by name or phone…"
            style={{ width: 240, borderRadius: 100, padding: '9px 16px' }}
          />
          {canImport ? (
            <Button variant="outline" onClick={() => setImporting(true)}>
              Import CSV
            </Button>
          ) : null}
          <Button onClick={() => setCreating(true)}>New client</Button>
        </div>
      </div>

      <div className="screen-scroll">
        <div className="screen-pad clients">
          <div className="clients__list">
            {isLoading && !clients.length ? (
              <div className="muted" style={{ padding: 16 }}>
                Loading…
              </div>
            ) : clients.length === 0 ? (
              <EmptyState
                title="No clients found"
                description={
                  debouncedQ ? 'Try a different search.' : 'Add your first client to get started.'
                }
                action={<Button onClick={() => setCreating(true)}>New client</Button>}
              />
            ) : (
              clients.map((c) => {
                const pts = patchTestState(c);
                return (
                  <button
                    key={c.id}
                    className={`client-row${selected?.id === c.id ? ' client-row--active' : ''}`}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <Avatar initials={initials(fullName(c.firstName, c.lastName))} tone="slate" />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="client-row__name">{fullName(c.firstName, c.lastName)}</div>
                      <div className="client-row__sub">{maskPhone(c.phone)}</div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                      }}
                    >
                      <span className="mono" style={{ fontSize: 12 }}>
                        {money(c.lifetimeValue)}
                      </span>
                      {pts === 'due' ? <Pill tone="danger">Patch test due</Pill> : null}
                    </div>
                  </button>
                );
              })
            )}

            {total > CLIENTS_PAGE ? (
              <div className="clients__pager">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - CLIENTS_PAGE))}
                >
                  ‹ Prev
                </Button>
                <span className="muted">
                  {offset + 1}–{Math.min(offset + CLIENTS_PAGE, total)} of {total}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + CLIENTS_PAGE >= total}
                  onClick={() => setOffset((o) => o + CLIENTS_PAGE)}
                >
                  Next ›
                </Button>
              </div>
            ) : null}
          </div>

          <div className="clients__detail">
            {selected ? (
              <ClientDetail
                client={selected}
                isOwnBook={selected.renterId != null && selected.renterId === user?.renterId}
                onEdit={() => setEditing(selected)}
              />
            ) : (
              <div className="muted" style={{ padding: 24 }}>
                Select a client to see their profile.
              </div>
            )}
          </div>
        </div>
      </div>

      {creating ? <ClientModal onClose={() => setCreating(false)} /> : null}
      {importing ? <ImportClientsModal onClose={() => setImporting(false)} /> : null}
      {editing ? <ClientModal existing={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function ClientDetail({
  client,
  isOwnBook,
  onEdit,
}: {
  client: Client;
  isOwnBook: boolean;
  onEdit: () => void;
}) {
  const pts = patchTestState(client);
  const scope =
    client.renterId == null ? 'Shared salon book' : isOwnBook ? 'Your sealed book' : 'Sealed book';

  return (
    <div className="client-detail">
      <div className="client-detail__head">
        <Avatar
          initials={initials(fullName(client.firstName, client.lastName))}
          tone="ink"
          size={48}
        />
        <div style={{ flex: 1 }}>
          <div className="client-detail__name">{fullName(client.firstName, client.lastName)}</div>
          <div className="muted">{client.phone ?? 'No phone on file'}</div>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Pill tone="neutral">{scope}</Pill>
        {client.marketingConsent ? <Pill tone="success">Marketing OK</Pill> : null}
        {pts === 'due' ? (
          <Pill tone="danger">Patch test due</Pill>
        ) : pts === 'soon' ? (
          <Pill tone="soft">Patch test soon</Pill>
        ) : null}
      </div>

      <div className="client-detail__stats">
        <div>
          <span className="slm-stat__label">Lifetime value</span>
          <div className="client-detail__stat">{money(client.lifetimeValue)}</div>
        </div>
        <div>
          <span className="slm-stat__label">Visits</span>
          <div className="client-detail__stat">{client.visitCount}</div>
        </div>
      </div>

      {/* WS-13 — read-only loyalty balance + recent history (renders only when the salon runs a
          program). Points are earned at the till and redeemed/adjusted on their own surfaces. */}
      <LoyaltyBalanceStrip clientId={client.id} />

      {/* WS-13 Feature 3b — recurring memberships: status, next renewal/period end, member benefit, with
          a one-click end-of-period cancel. Renders only when the client holds a membership. */}
      <MembershipStrip clientId={client.id} />

      {/* WS-13 Feature 4 — consent & consultations (LRU-07): the client's signed consent_records with a
          derived validity badge, patch-test validity surfaced prominently, a staff-level CAPTURE flow
          (active template → typed-name + explicit confirm), and a frozen-snapshot record viewer. */}
      <ConsentPanel clientId={client.id} clientName={fullName(client.firstName, client.lastName)} />

      {/* WS-13 Feature 5 — before/after photos (LRU-15): the client's photo SETS (grouped by pair),
          a camera/file capture flow, a per-set visibility selector (showcase gated on portfolio
          consent + manager), and a typed-confirm delete. Honestly shows a "storage not configured"
          message instead of a broken uploader when the inert staging bucket signals 503. */}
      <PhotoGallery clientId={client.id} clientName={fullName(client.firstName, client.lastName)} />

      {client.colourFormula ? (
        <div className="client-detail__block">
          <span className="slm-stat__label">Colour formula</span>
          <div className="mono" style={{ fontSize: 13.5, marginTop: 4 }}>
            {client.colourFormula}
          </div>
        </div>
      ) : null}

      {client.notes ? (
        <div className="client-detail__block">
          <span className="slm-stat__label">Notes</span>
          <div style={{ fontSize: 13.5, marginTop: 4, lineHeight: 1.5 }}>{client.notes}</div>
        </div>
      ) : null}

      {client.email ? (
        <div className="muted" style={{ fontSize: 13 }}>
          {client.email}
        </div>
      ) : null}
    </div>
  );
}
